import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Calendar, MapPin, ArrowRight, Sparkles } from 'lucide-react';
import { projectData } from '@/data/mock';
import Countdown from '@/components/Countdown';

const HomePage = ({ onNavigate }) => {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  return (
    <div className="relative min-h-full w-full">
      {/* Hero Background Image */}
      <div className="absolute inset-0">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('${projectData.images.hero}')`,
            backgroundPosition: 'center'
          }}
        />
        {/* Lighter overlay - photo more prominent, gradient only at edges for text legibility */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0d1a0d]/50 via-[#0d1a0d]/30 to-[#0d1a0d]/85" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0d1a0d]/40 to-transparent" />
      </div>

      {/* Visualisierung label */}
      <span className="absolute top-5 right-5 z-20 text-[10px] uppercase tracking-widest text-white/70 bg-black/40 backdrop-blur-sm px-3 py-1 rounded-full border border-white/10">
        Visualisierung
      </span>

      {/* AI MODIFIED badge (Art. 50 (4) EU-KI-VO) — always visible overlay */}
      <img
        src="/badges/ai_modified_white.png"
        alt="Mit KI bearbeitet (AI MODIFIED)"
        data-testid="ai-badge-hero"
        className="absolute top-5 left-5 z-20 h-6 md:h-7 w-auto opacity-95 drop-shadow-lg"
      />

      {/* Gold Accent Lines */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent opacity-50" />
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent opacity-50" />

      {/* Content */}
      <div className="relative z-10 min-h-full flex flex-col justify-center items-center text-center px-5 sm:px-8 md:px-16 py-16 md:py-8">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isLoaded ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="mb-6 md:mb-8"
        >
          {/* Decorative Element */}
          <div className="flex items-center justify-center gap-4 mb-6 md:mb-8">
            <div className="w-10 sm:w-16 h-px bg-gradient-to-r from-transparent to-[#D4AF37]" />
            <Sparkles className="w-5 h-5 md:w-6 md:h-6 text-[#D4AF37]" />
            <div className="w-10 sm:w-16 h-px bg-gradient-to-l from-transparent to-[#D4AF37]" />
          </div>

          <p className="text-[#F4E4C1] text-xs sm:text-sm md:text-base tracking-[0.3em] sm:tracking-[0.4em] uppercase mb-5 md:mb-8 font-light">
            Berlin-Schmöckwitz
          </p>

          <h1 className="text-5xl sm:text-7xl md:text-8xl lg:text-9xl xl:text-[10rem] font-light text-white mb-5 md:mb-8 tracking-tight" style={{ fontFamily: "'Cormorant', serif", textShadow: '0 4px 30px rgba(0,0,0,0.6)' }}>
            Green Living
          </h1>

          <div className="h-px w-24 md:w-32 mx-auto mb-5 md:mb-8 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />

          <h2 className="text-2xl sm:text-4xl md:text-6xl font-light text-white/95 mb-6 md:mb-10" style={{ fontFamily: "'Cormorant', serif", textShadow: '0 2px 20px rgba(0,0,0,0.6)' }}>
            Leben am Wasser
          </h2>

          <p className="text-base sm:text-xl md:text-2xl text-[#F4E4C1]/90 max-w-3xl mx-auto mb-8 md:mb-12 font-light leading-relaxed px-2" style={{ letterSpacing: '0.05em', textShadow: '0 2px 15px rgba(0,0,0,0.7)' }}>
            {projectData.tagline}
          </p>
        </motion.div>

        {/* Countdown to Besichtigungstermin */}
        <div className="mb-10 md:mb-14 w-full max-w-3xl mx-auto">
          <Countdown />
        </div>

        {/* Info Cards */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isLoaded ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.9, delay: 0.7 }}
          className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6 max-w-3xl mx-auto mb-8 md:mb-12 w-full"
        >
          <button
            type="button"
            onClick={onNavigate ? () => onNavigate('kontakt') : undefined}
            data-testid="home-termin-cta"
            className="luxury-card luxury-blur rounded-2xl p-6 md:p-8 border-[#D4AF37]/20 hover:border-[#D4AF37]/50 transition-all duration-500 block cursor-pointer text-center w-full"
          >
            <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-gradient-to-br from-[#D4AF37]/20 to-[#C9A961]/20 flex items-center justify-center mb-3 md:mb-4 mx-auto">
              <Calendar className="w-6 h-6 md:w-7 md:h-7 text-[#D4AF37]" />
            </div>
            <p className="text-[#F4E4C1]/70 text-xs md:text-sm mb-2 uppercase tracking-widest">Besichtigungstermin</p>
            <p className="text-white text-xl md:text-2xl font-light mb-1" style={{ fontFamily: "'Cormorant', serif" }}>
              {projectData.besichtigungstermin.date}
            </p>
            <p className="text-white/80 text-sm md:text-base">{projectData.besichtigungstermin.time}</p>
            <p className="mt-3 text-[10px] uppercase tracking-widest text-[#D4AF37]/80">Jetzt anmelden ↗</p>
          </button>

          <a
            href="https://www.google.com/maps/search/?api=1&query=Lindenstra%C3%9Fe+39+A-D%2C+12527+Berlin-Schm%C3%B6ckwitz"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="home-standort-link"
            className="luxury-card luxury-blur rounded-2xl p-6 md:p-8 border-[#D4AF37]/20 hover:border-[#D4AF37]/50 transition-all duration-500 block cursor-pointer"
          >
            <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-gradient-to-br from-[#D4AF37]/20 to-[#C9A961]/20 flex items-center justify-center mb-3 md:mb-4 mx-auto">
              <MapPin className="w-6 h-6 md:w-7 md:h-7 text-[#D4AF37]" />
            </div>
            <p className="text-[#F4E4C1]/70 text-xs md:text-sm mb-2 uppercase tracking-widest">Standort</p>
            <p className="text-white text-sm md:text-base font-light leading-relaxed">
              {projectData.location}
            </p>
            <p className="mt-3 text-[10px] uppercase tracking-widest text-[#D4AF37]/80">Auf Karte öffnen ↗</p>
          </a>
        </motion.div>

        {/* CTA Button - only on desktop; on mobile the bottom nav provides Kontakt */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={isLoaded ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.7, delay: 1.1 }}
          className="hidden lg:block"
        >
          <button
            data-testid="home-cta-button"
            onClick={onNavigate ? () => onNavigate('kontakt') : undefined}
            className="luxury-button group relative px-8 md:px-10 py-4 md:py-5 text-white rounded-full transition-all duration-500 overflow-hidden"
          >
            <span className="relative z-10 flex items-center gap-3">
              <span className="text-sm md:text-base tracking-wider uppercase font-light">Kontakt aufnehmen</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </span>
          </button>
        </motion.div>

        {/* Subtle Badge */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isLoaded ? { opacity: 1 } : {}}
          transition={{ duration: 0.6, delay: 1.5 }}
          className="mt-8 md:mt-16 flex flex-col sm:flex-row items-center gap-2 sm:gap-3 text-[#F4E4C1]/60 text-xs md:text-sm"
        >
          <span className="uppercase tracking-widest">28 Wohnungen</span>
          <span className="hidden sm:block w-1 h-1 rounded-full bg-[#D4AF37]" />
          <span className="uppercase tracking-widest">KfW-40 Standard</span>
        </motion.div>
      </div>

      {/* Grain Texture */}
      <div className="grain-texture absolute inset-0 pointer-events-none" />
    </div>
  );
};

export default HomePage;
